// src/pages/auth/ForgotPassword.jsx
//
// Pre-auth password reset flow. Matches the dark lime/pitch design
// system used by Login + Register so the three pre-auth pages feel
// like one product. Sends a Firebase password reset email and shows
// a deliberately vague success message regardless of whether the
// account actually exists (account-enumeration defense).
'use client';

import { useState } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app as firebaseApp } from '@/firebase/firebase';
import AuthShell from '@/components/auth/AuthShell';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function ErrorBanner({ message, onClose }) {
  if (!message) return null;
  return (
    <div
      role="alert"
      className="mb-5 flex items-start gap-3 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200"
    >
      <span className="mt-0.5 inline-block h-2.5 w-2.5 shrink-0 rounded-full bg-red-400" />
      <div className="flex-1">{message}</div>
      <button
        type="button"
        onClick={onClose}
        className="rounded p-1 text-red-300 hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-red-400"
        aria-label="Dismiss error"
      >
        ×
      </button>
    </div>
  );
}

function SuccessBanner({ message }) {
  if (!message) return null;
  return (
    <div
      role="status"
      className="mb-5 flex items-start gap-3 rounded-lg border border-[#b5f03d]/40 bg-[#b5f03d]/10 px-4 py-3 text-sm text-[#b5f03d]"
    >
      <span className="mt-0.5 inline-block h-2.5 w-2.5 shrink-0 rounded-full bg-[#b5f03d]" />
      <div className="flex-1">
        <p
          className="font-bold mb-1"
          style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
        >
          CHECK YOUR INBOX
        </p>
        <p className="text-[#b5f03d]/90">{message}</p>
      </div>
    </div>
  );
}

export default function ForgotPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const backTo = (location.state && location.state.from) || '/login';

  const [email, setEmail] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [formError, setFormError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const emailValid = EMAIL_RE.test(email.trim());
  const canSubmit = emailValid && !isSubmitting;

  // Calls our custom cloud function instead of Firebase Auth's
  // sendPasswordResetEmail. Why?
  //   1. Firebase's default email sends from
  //      noreply@scrimmage-connect.firebaseapp.com, which has no SPF
  //      alignment with getscrimly.com → goes to spam.
  //   2. The default subject line literally reads "Reset your password
  //      for project-903076627622" — looks like a phishing email.
  //   3. We want full lime/pitch brand styling so the reset email
  //      feels like part of the same product.
  // The cloud function generates the secure oobCode via the Admin SDK
  // and ships our own SendGrid template (templates.getPasswordResetHtml)
  // from noreply@getscrimly.com. It always returns { ok: true } for
  // account-enumeration defense — we never tell the caller whether
  // the email exists.
  async function handleSubmit(e) {
    e.preventDefault();
    setFieldError('');
    setFormError('');
    setSuccessMessage('');

    const trimmed = email.trim();

    if (!trimmed) {
      setFieldError('Enter the email you use for Scrimly.');
      return;
    }
    if (!emailValid) {
      setFieldError('Please enter a valid email address.');
      return;
    }

    setIsSubmitting(true);
    try {
      const functions = getFunctions(firebaseApp);
      const sendCustomPasswordReset = httpsCallable(
        functions,
        'sendCustomPasswordReset'
      );
      await sendCustomPasswordReset({ email: trimmed });

      // Always show success — the cloud function returns ok:true
      // regardless of whether the account exists, by design.
      setSuccessMessage(
        `If an account exists for ${trimmed}, a reset link is on its way. ` +
        `It usually arrives within a minute. Check spam or promotions if you do not see it.`
      );
    } catch (err) {
      console.error('Password reset error', err);
      // The only errors that reach us here are network / function
      // unreachable. Firebase HTTPs errors come through as
      // err.code (e.g., 'functions/unavailable').
      setFormError(
        'We could not start the reset process. Check your connection and try again in a moment.'
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  const inputClass =
    'w-full min-h-[52px] rounded-[10px] border border-white/10 bg-white/5 px-4 py-3.5 text-[15px] text-white placeholder:text-white/30 transition-colors focus:border-[#b5f03d] focus:bg-white/[0.08] focus:outline-none disabled:opacity-60';

  return (
    <AuthShell>
            <div className="text-center">
              <span className="inline-flex items-center rounded-full border border-[#b5f03d]/30 bg-[#b5f03d]/10 px-4 py-1.5 text-[12px] font-semibold uppercase tracking-[0.5px] text-[#b5f03d]">
                Reset password
              </span>

              <h1
                className="mt-5 uppercase leading-none text-white"
                style={{
                  fontFamily: HEAD_FONT,
                  fontWeight: 900,
                  fontSize: 'clamp(30px, 3.8vw, 42px)',
                  letterSpacing: '-0.5px',
                }}
              >
                Forgot your{' '}
                <span className="text-[#b5f03d] not-italic">password?</span>
              </h1>

              <p className="mt-3.5 text-[15.5px] leading-relaxed text-white/65">
                Enter the email you use for Scrimly and we will send a link
                to set a new one.
              </p>
            </div>

            <div className="mt-7">
              <ErrorBanner
                message={formError}
                onClose={() => setFormError('')}
              />
              <SuccessBanner message={successMessage} />

              {!successMessage && (
                <form
                  onSubmit={handleSubmit}
                  className="space-y-4 text-left"
                  noValidate
                >
                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor="email"
                      className="text-[13px] font-bold uppercase tracking-[1.2px] text-white/80"
                      style={{ fontFamily: HEAD_FONT }}
                    >
                      Email
                    </label>
                    <input
                      id="email"
                      type="email"
                      inputMode="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (fieldError) setFieldError('');
                        if (formError) setFormError('');
                      }}
                      required
                      autoFocus
                      autoComplete="email"
                      placeholder="coach@yourteam.com"
                      aria-invalid={
                        !!fieldError || (email.length > 0 && !emailValid)
                      }
                      disabled={isSubmitting}
                      className={inputClass}
                    />
                    {fieldError && (
                      <p className="mt-1 text-xs text-red-300">{fieldError}</p>
                    )}
                    {!fieldError && email.length > 0 && !emailValid && (
                      <p className="mt-1 text-xs text-red-300">
                        Please enter a valid email address.
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={!canSubmit}
                    aria-busy={isSubmitting}
                    className="mt-2 inline-flex w-full min-h-[52px] items-center justify-center rounded-[10px] bg-[#b5f03d] px-6 py-4 uppercase tracking-[0.5px] text-[#0d1f14] transition-all hover:bg-[#94cc28] hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(181,240,61,0.25)] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none"
                    style={{
                      fontFamily: HEAD_FONT,
                      fontWeight: 800,
                      fontSize: 18,
                    }}
                  >
                    {isSubmitting ? 'Sending reset link…' : 'Send reset link'}
                  </button>
                </form>
              )}

              {successMessage && (
                <button
                  type="button"
                  onClick={() => navigate(backTo)}
                  className="mt-2 inline-flex w-full min-h-[52px] items-center justify-center rounded-[10px] bg-[#b5f03d] px-6 py-4 uppercase tracking-[0.5px] text-[#0d1f14] transition-all hover:bg-[#94cc28] hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(181,240,61,0.25)]"
                  style={{
                    fontFamily: HEAD_FONT,
                    fontWeight: 800,
                    fontSize: 18,
                  }}
                >
                  Back to sign in
                </button>
              )}

              {!successMessage && (
                <p className="mt-6 text-center text-sm text-white/55">
                  Remembered it?{' '}
                  <RouterLink
                    to={backTo}
                    className="font-semibold text-[#b5f03d] hover:underline"
                  >
                    Back to sign in
                  </RouterLink>
                </p>
              )}

              <p className="mt-2 text-center text-xs text-white/40">
                Need an account?{' '}
                <RouterLink
                  to="/register"
                  className="font-semibold text-[#b5f03d]/80 hover:text-[#b5f03d] hover:underline"
                >
                  Create one
                </RouterLink>
              </p>

              <p className="mt-4 text-center text-[11px] text-white/35">
                Still stuck? Email{' '}
                <a
                  href="mailto:hello@getscrimly.com"
                  className="font-semibold text-[#b5f03d]/80 hover:text-[#b5f03d] hover:underline"
                >
                  hello@getscrimly.com
                </a>{' '}
                and we will help you back in.
              </p>
            </div>
    </AuthShell>
  );
}
